import React, {PureComponent} from 'react';
import {NotificationManager} from 'react-notifications';
import FileSaver from 'filesaver.js';

import * as api from '../lib/api';
import SideModal from './SideModal';
import Form from './Form';
import Button from './Button';
import DateTimeInput from './DateTimeInput';
import Tabs from './Tabs';
import {withUser} from '../lib/user';
import Input from './Input';
import { T } from '../components/I18n';

class ExportFormDataModal extends PureComponent {
  state = {
    tab: "Export",
    exportData: {
      startDate: null,
      endDate: null,      
    },
    schedule: {
      jobId: null,
      interval: "NEVER",
      timeInterval: "",
      scheduleTime: null
    },
    exporting: false
  }

  async componentDidUpdate(prevProps, prevState) {
    const {form, credentials} = this.props;
    if(form && (!prevProps.form || (prevProps.form && prevProps.form.id !== form.id))) {
      const schedules = await api.getScheduledTemplateExports(form, credentials);
      const schedule = schedules[0] || {
        jobId: null,
        interval: "NEVER",
        timeInterval: "",
        scheduleTime: null
      }
      this.setState({schedule});
    }
  }

  closeModal() {
    const {onClose} = this.props;
    onClose();
    this.setState({
      tab:"Export",
      schedule: {
        jobId: null,
        interval: "NEVER",
        timeInterval: "",
        scheduleTime: null
    }
    })
  }

  renderTabContent() {
    const {tab, exportData, schedule} = this.state;
    switch (tab) {
      case "Export": 
        return (<ExportDataTab exportData={exportData} onChange={exportData => this.setState({exportData})}/>);
      case "Schedule":
        return (<ScheduleExportDataTab schedule={schedule} onChange={schedule => this.setState({schedule})}/>);
      default:
        throw new Error("unknown Tab");
    }
  }

  renderTabs() {
    const {tab} = this.state;
    return (
      <Tabs>
        <Tabs.Tab onClick={() => this.setState({tab:"Export"})} active={tab === "Export"}>Export</Tabs.Tab>
        <Tabs.Tab onClick={() => this.setState({tab:"Schedule"})} active={tab === "Schedule"}>Schedule</Tabs.Tab>
      </Tabs>
    )
  }

  renderOptions() {
    switch(this.state.tab) {
      case "Export": 
        return [
          <Button onClick={() => this.handleExportData()} disabled={!this.canSubmit()}>Export</Button>
        ];
      case "Schedule":
        return [
          <Button onClick={() => this.handleSaveSchedule()} disabled={!this.canSubmit()}>Save</Button>
        ];
      default:
        return [];
    }
  }

  canSubmit() {
    const {tab, exporting, exportData, schedule} = this.state;
    if (exporting) {
      return false;
    }

    switch(tab) {
      case "Export": 
        return exportData.startDate && exportData.endDate;
      case "Schedule":
        if (schedule.interval === "NEVER" && schedule.id) {
          return true;
        }

        return schedule.interval && schedule.scheduleTime && schedule.timeInterval;
      default:
        return false;
    }
  }

  handleExportData = async () => {
    const {form, credentials} = this.props;
    const {exportData: {startDate, endDate}} = this.state;

    this.setState({exporting: true});

    try {
      const content = await api.exportSingleTemplateData(form.id, startDate, endDate, credentials);

      this.setState({startDate: null, endDate: null});
      this.closeModal();
      setTimeout(() => FileSaver.saveAs(content, `${form.name}-${Date.now()}.xlsx`), 250);
    }
    catch (e) {
      if(e.status === 400) {
        NotificationManager.error('Too much data to export. Reduce your date range and try again.');
      }
      else {
        NotificationManager.error('Unable to export form data. Please try again.');
      }
    }
    finally {
      this.setState({exporting: false});
    }
  }

  handleSaveSchedule = async () => {
    if (this.state.schedule.interval === "NEVER") {
      await this.deleteSchedule();
    }
    else {
      await this.saveSchedule();
    }
  }

  saveSchedule = async () => {
    const {form, credentials} = this.props;
    const {schedule} = this.state;

    this.setState({exporting: true});

    try {
      const updatedSchedule = await api.saveTemplateExportSchedule(form, schedule, credentials);
      this.setState({schedule: updatedSchedule, exporting: false});
      NotificationManager.success(`Your schedule has been ${schedule.jobId ? "updated" : "created"}.`)
    }
    catch(e) {
      NotificationManager.error('Could not schedule this export.');
      this.setState({exporting: false});
    }
  }

  deleteSchedule = async () => {
    const {form, credentials} = this.props;
    const {schedule} = this.state;
    this.setState({exporting: true});

    try {
      await api.deleteTemplateExportSchedule(form, schedule, credentials);
      NotificationManager.success('Your schedule has been deleted.');
      this.setState({schedule: {
        jobId: null,
        interval: "NEVER",
        timeInterval: "",
        scheduleTime: null
      }, exporting: false});
    }
    catch (e) {
      NotificationManager.error('Could not delete the scheduled export');
      this.setState({exporting: false});
    }
  }

  render() {
    const {form} = this.props;
    if (!form) {
      return null;
    }

    return (
      <SideModal
        title={form.name}
        open={form}
        onClose={() => this.closeModal()}
        tabs={this.renderTabs()}
        options={this.renderOptions()}>
          <SideModal.Content>
            {this.renderTabContent()}
          </SideModal.Content>
      </SideModal>
    );
  }
}

function ExportDataTab({exportData, onChange}) {
  const {startDate, endDate} = exportData;
  return (
    <>
      <Form.Group>
        <DateTimeInput
          label="From"
          placeholder="Choose a date &amp; time"
          date={startDate}
          onChange={startDate => onChange({startDate, endDate})}
        />
      </Form.Group>
      <Form.Group>
        <DateTimeInput
          label="To"
          placeholder="Choose a date &amp; time"
          date={endDate}
          onChange={endDate => onChange({startDate, endDate})}
        />
      </Form.Group>
    </>
  )
}

function ScheduleExportDataTab({schedule, onChange}) {
  const {jobId, interval, timeInterval, scheduleTime} = schedule;
  let dayString = "days";
  if (timeInterval && timeInterval === '1') {
    dayString = "day";
  }
  return (
    <>
    <Form.Group>
      <Input 
        id="frequency"
        type="select"
        label="Export frequency"
        value={interval}
        onChange={e => onChange({jobId, interval: e.target.value, timeInterval, scheduleTime})}
        >
          <option value="NEVER">Never</option>
          <option value="DAY">Day</option>
          <option value="WEEK">Week</option>
          <option value="MONTH">Month</option>
      </Input>
    </Form.Group>
    {interval !== "NEVER" &&
      <>
        <Form.Group>
          <DateTimeInput
            label="Start Date"
            placeholder="Choose a date &amp; time"
            date={scheduleTime ? new Date(scheduleTime) : scheduleTime}
            onChange={scheduleTime => onChange({jobId, interval, timeInterval, scheduleTime})}
            helpText="When do you want to receive your first scheduled export?"
          />
        </Form.Group>
        <Form.Group>
          <Input
            id="timeInterval"
            label="Export data from"
            placeholder="Choose the data you want"
            type="number"
            value={timeInterval}
            helpText={<T isolate>Your scheduled export will contain data from the previous<T variable="daysCount"> {timeInterval === "" ? "N" : timeInterval}</T> {dayString}</T>}
            onChange={e => onChange({jobId, interval, timeInterval: e.target.value >= 0 ? e.target.value : 0, scheduleTime})}
            />
        </Form.Group>
      </>
    }
    </>
  )
}

export default withUser(ExportFormDataModal);